import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Button, FormControl, FormGroup, FormLabel, Modal, Spinner } from 'react-bootstrap';

// Admin Dashboard → Accounts (2026-10-09 b): which OpenAI project an account
// runs on. Every account runs on the shared default project unless an admin
// gives it a dedicated one — an existing project of the organization, or a new
// one — so a large account's usage can be audited on its own. Vector stores
// live inside a project, so the choice can change (back to the default too)
// only while the account holds no memory; the server answers 409 with the
// reasons once it does. Routes: /api/cux/accounts/openai-project* (admin only).

const ENV_LABEL = process.env.REACT_APP_WENV === 'prod' ? 'Prod' : 'Dev';

/** The organization's projects, and whether dedicated projects are set up here. */
export function useOpenAiProjects(appUrl, { enabled = true } = {}) {
  const [state, setState] = useState({ loading: true, configured: false, projects: [], error: '' });
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    axios.get(`${appUrl}/api/cux/accounts/openai-projects`, { withCredentials: true })
      .then(({ data }) => { if (live) setState({ loading: false, configured: Boolean(data?.configured), projects: Array.isArray(data?.projects) ? data.projects : [], error: data?.error || '' }); })
      .catch((err) => { if (live) setState({ loading: false, configured: false, projects: [], error: err?.response?.data?.message || 'Could not load the OpenAI projects.' }); });
    return () => { live = false; };
  }, [appUrl, enabled]);
  return state;
}

/** shop → { projectId, projectName } for accounts on a dedicated project. */
export function useOpenAiAssignments(appUrl, refreshKey = 0) {
  const [map, setMap] = useState({});
  useEffect(() => {
    let live = true;
    axios.get(`${appUrl}/api/cux/accounts/openai-project-assignments`, { withCredentials: true })
      .then(({ data }) => {
        if (!live) return;
        const next = {};
        (data?.assignments || []).forEach((a) => { next[a.shop] = a; });
        setMap(next);
      })
      .catch(() => {});
    return () => { live = false; };
  }, [appUrl, refreshKey]);
  return map;
}

/** The body field the create / change routes take, or null for the default project. */
export function openAiChoiceBody(choice) {
  if (!choice || choice.mode === 'default') return null;
  if (choice.mode === 'existing') return { mode: 'existing', projectId: choice.projectId };
  return { mode: 'new', projectName: (choice.projectName || '').trim() };
}

/** A choice that can be sent: an existing project picked, or a new one named. */
export function openAiChoiceComplete(choice) {
  if (!choice || choice.mode === 'default') return true;
  if (choice.mode === 'existing') return Boolean(choice.projectId);
  return Boolean((choice.projectName || '').trim());
}

/** Default / existing / new project fields, used by Create Account and the Change dialog. */
export function OpenAiProjectFields({ value, onChange, projects, configured, loading, accountName = '', disabled = false, idPrefix = 'openai' }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const pickMode = (mode) => set({ mode, projectId: mode === 'existing' ? (value.projectId || '') : '', projectName: mode === 'new' ? (value.projectName || `${accountName || 'Account'} - ${ENV_LABEL}`) : '' });
  return (
    <FormGroup className="mb-3" data-testid={`${idPrefix}-project-fields`}>
      <FormLabel className="admin-label">OpenAI project:</FormLabel>
      <FormControl as="select" className="admin-input" value={value.mode} onChange={(e) => pickMode(e.target.value)} disabled={disabled || loading} aria-label="OpenAI project">
        <option value="default">Default project (shared)</option>
        <option value="existing" disabled={!configured}>An existing OpenAI project</option>
        <option value="new" disabled={!configured}>A new OpenAI project</option>
      </FormControl>
      {value.mode === 'existing' && (
        <FormControl as="select" className="admin-input mt-2" value={value.projectId || ''} onChange={(e) => set({ projectId: e.target.value })} disabled={disabled} aria-label="Existing OpenAI project">
          <option value="">Choose a project…</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}{p.accounts ? ` · ${p.accounts} account${p.accounts === 1 ? '' : 's'}` : ''}</option>
          ))}
        </FormControl>
      )}
      {value.mode === 'new' && (
        <FormControl type="text" className="admin-input mt-2" value={value.projectName || ''} maxLength={100} onChange={(e) => set({ projectName: e.target.value })} disabled={disabled} placeholder={`${accountName || 'Account'} - ${ENV_LABEL}`} aria-label="New OpenAI project name" />
      )}
      <small className="text-muted d-block mt-1">
        {configured
          ? 'Large accounts can run on their own OpenAI project, so their usage can be audited separately. The account gets its own project key. The project can be changed only until the account has memory.'
          : 'Only the default project is available: dedicated projects need OPENAI_ADMIN_API_KEY and CF_OPENAI_KEYS_MASTER_KEY on the API server.'}
      </small>
    </FormGroup>
  );
}

/** Change one account's OpenAI project (Admin Dashboard → Accounts → OpenAI project). */
export function OpenAiProjectDialog({ appUrl, account, onClose, onSaved }) {
  const shop = account?.shop;
  const { projects, configured, loading: projectsLoading } = useOpenAiProjects(appUrl);
  const [current, setCurrent] = useState(null);
  const [choice, setChoice] = useState({ mode: 'default', projectId: '', projectName: '' });
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let live = true;
    axios.get(`${appUrl}/api/cux/accounts/${encodeURIComponent(shop)}/openai-project`, { withCredentials: true })
      .then(({ data }) => {
        if (!live) return;
        setCurrent(data);
        setChoice(data?.mode === 'dedicated' ? { mode: 'existing', projectId: data.projectId, projectName: '' } : { mode: 'default', projectId: '', projectName: '' });
      })
      .catch((err) => { if (live) setLoadError(err?.response?.data?.message || 'Could not read this account’s OpenAI project.'); });
    return () => { live = false; };
  }, [appUrl, shop]);

  const unchanged = current && (
    (current.mode !== 'dedicated' && choice.mode === 'default')
    || (current.mode === 'dedicated' && choice.mode === 'existing' && choice.projectId === current.projectId)
  );
  const locked = Boolean(current?.locked);

  const save = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const body = openAiChoiceBody(choice) || { mode: 'default' };
      const { data } = await axios.put(`${appUrl}/api/cux/accounts/${encodeURIComponent(shop)}/openai-project`, { ...body, accountName: account?.shop_name || shop }, { withCredentials: true });
      onSaved?.(data);
    } catch (err) {
      setSaveError(err?.response?.data?.message || 'The OpenAI project could not be changed.');
      if (err?.response?.status === 409 && err.response.data?.reasons) setCurrent((c) => ({ ...(c || {}), locked: true, reasons: err.response.data.reasons }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={() => { if (!saving) onClose?.(); }} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.05rem' }}>OpenAI project · {account?.shop_name || shop}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadError && <Alert variant="danger">{loadError}</Alert>}
        {!current && !loadError && <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>}
        {current && (
          <>
            <p className="mb-3" style={{ fontSize: '0.9rem' }}>
              Runs on: <strong>{current.mode === 'dedicated' ? (current.projectName || current.projectId) : 'Default project (shared)'}</strong>
              {current.mode === 'dedicated' && current.assignedAt ? <span className="text-muted"> · since {new Date(current.assignedAt).toLocaleDateString()}</span> : null}
            </p>
            {locked && (
              <Alert variant="secondary" data-testid="openai-project-locked">
                This account already has memory stored in its current OpenAI project
                {Array.isArray(current.reasons) && current.reasons.length ? ` (${current.reasons.map((r) => r.text).join(', ')})` : ''}, so its project can no
                longer change. Memory lives inside a project and would be lost to the account.
              </Alert>
            )}
            <OpenAiProjectFields
              value={choice}
              onChange={setChoice}
              projects={projects}
              configured={configured}
              loading={projectsLoading}
              accountName={account?.shop_name || shop}
              disabled={locked || saving}
              idPrefix="openai-change"
            />
            {saveError && <Alert variant="danger" className="mb-0">{saveError}</Alert>}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={!current || locked || saving || unchanged || !openAiChoiceComplete(choice)} data-testid="openai-project-save">
          {saving ? 'Saving…' : choice.mode === 'default' && current?.mode === 'dedicated' ? 'Switch to the default project' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
